/**
 * The paperwork a line's ERP jobs can produce — Certificate of Conformity and
 * Despatch Note.
 *
 * Only rendered on the records path: both documents are built from imported
 * job rows, so a line still falling back to change-log parsing has nothing to
 * print. Asking the backend anyway would return a 400 the user cannot act on.
 */
import { useState } from 'react';
import { ExternalLink, FileBadge, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { purchasingAPI } from '../../lib/api';
import { Button } from '../ui/button';
import type { LucideIcon } from 'lucide-react';

/** The documents generated from a line's ERP jobs. */
type DocumentKind = 'certificate' | 'despatch-note';

interface JobDocumentButtonsProps {
  /** PO line the documents are generated for. */
  lineId: number;
}

/**
 * Buttons that fetch a generated PDF and open it in a new tab.
 *
 * @param lineId - the PO line whose jobs the documents cover
 */
export default function JobDocumentButtons({ lineId }: JobDocumentButtonsProps) {
  const [loading, setLoading] = useState<DocumentKind | null>(null);

  /**
   * Fetch a document and hand it to the browser.
   *
   * Opened via an anchor rather than window.open: after an await the call is
   * outside the user-gesture context and popup blockers swallow window.open,
   * which makes the button look dead. Deliberately no `download` attribute —
   * that would save the file instead, and these are meant to be read on
   * screen before anyone prints or sends them. The endpoint sets
   * Content-Disposition: inline to match.
   */
  const handleDocument = async (kind: DocumentKind) => {
    setLoading(kind);
    try {
      const response = kind === 'certificate'
        ? await purchasingAPI.getPOLineCertificate(lineId)
        : await purchasingAPI.getPOLineDespatchNote(lineId);
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err: unknown) {
      console.error('Failed to generate document:', err);
      toast.error(
        kind === 'certificate'
          ? 'Could not generate the Certificate of Conformity'
          : 'Could not generate the Despatch Note',
      );
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <DocumentButton
        icon={FileBadge}
        label="View Certificate of Conformity"
        busy={loading === 'certificate'}
        disabled={!!loading}
        onClick={() => handleDocument('certificate')}
      />
      <DocumentButton
        icon={FileText}
        label="View Despatch Note"
        busy={loading === 'despatch-note'}
        disabled={!!loading}
        onClick={() => handleDocument('despatch-note')}
      />
    </div>
  );
}

/**
 * A button that says what it will do.
 *
 * The pair previously read as bare document names, which left people unsure
 * whether clicking generated something, downloaded it, or filed it somewhere.
 * "View" states the outcome, the trailing arrow says it opens away from this
 * screen, and the busy state says "Generating…" because that is the wait the
 * spinner is covering — the PDF is built on demand, not fetched from storage.
 */
function DocumentButton({ icon: Icon, label, busy, disabled, onClick }: {
  icon: LucideIcon;
  label: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      title={`${label} — generated now and opened in a new tab`}
    >
      {busy
        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
        : <Icon className="h-3.5 w-3.5 mr-1.5" />}
      {busy ? 'Generating…' : label}
      {!busy && <ExternalLink className="h-3 w-3 ml-1.5 text-muted-foreground" aria-hidden="true" />}
    </Button>
  );
}
